/* eslint-disable @typescript-eslint/no-explicit-any */
import { IconButton, Menu, MenuItem } from "@mui/material";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CgMoreVerticalAlt } from "react-icons/cg";
import { MdFavoriteBorder } from "react-icons/md";
import { HeadersFunction, MetaFunction, useLoaderData, useLocation } from "react-router";
import type { VirtuosoHandle } from "react-virtuoso";
import { Virtuoso } from "react-virtuoso";
import BreadCrubComponent from "~/components/shared-components/BreadCrubComponent";
import Chip from "~/components/shared-components/Chip";
import ClientOnly from "~/components/shared-components/ClentOnly";
import ImageWithSkeleton from "~/components/shared-components/ImageWithSkeleton";
import LoadingSpinner from "~/components/shared-components/LoadingSpinner";
import NodataFound from "~/components/shared-components/NoDataFound";
import NoHasMoreText from "~/components/shared-components/NoHasMoreText";
import ShareBotton from "~/components/shared-components/ShareButton";
import Tag from "~/components/shared-components/Tag";
import useDisplayStickyButton from "~/hook/useDisplayStickyButton";
import { useFontStyle } from "~/hook/useFontStyle";
import useIsClient from "~/hook/useIsClient";
import { useLanguageParams } from "~/hook/useLanguageParams";
import useLocale from "~/hook/useLocale";
import { ImageData } from "~/interface/commonImagesize-types";
import { getEventImageDate } from "~/lib/helperFuncation/formateDate";
import { getPageMeta } from "~/lib/helperFuncation/metaHelper";
import { cn } from "~/lib/utils";
import API_PATH from "~/utils/api-routes";
import { BLUR_HASH, CACHED_PAGE, FALLBACK_IMAGE_URL, LANG } from "~/utils/constant";
import { computeEtagFromData, homeRouteString } from "~/utils/helper-funcations";
import api from "~/utils/Interceptor";
import { metaService } from "~/utils/metaService";

interface eventImagesData {
  id: number;
  name: string;
  desc: string;
  meta_title: string;
  meta_desc: string;
  place: number;
  type: string;
  from_date: string;
  to_date: string;
  thumb_img: string;
  url_slug: string;
  n_sub_event: number;
  year: number;
  multi_event: boolean;
  tag: string | null;
  img_aspect: string;
  highlight_video_id: string | null;
  is_series: boolean | null;
  auto_seo: boolean | null;
  img: ImageData | null;
}

interface placeData {
  id: number;
  text: string;
  thumb_img: string;
}

interface imageCarddata {
  image: eventImagesData;
  place: placeData[];
  pathName: string;
}

interface loaderData {
  years: number[];
  availableyearsArray: number[];
  eventImages: eventImagesData[];
  place: placeData[];
  selectedYear: number;
}

type ImageCardButtonsProps = {
  shareUrl: string;
  shareTitle: string;
};

export const loader = async ({ params, request }: { params: any; request: Request }) => {
  const { lang } = params;
  const language = lang === LANG.gujarati ? LANG.gujarati : LANG.english;
  try {
    //fetch selected from url
    const selectedYear = params.year || 0;

    //fetch places from masterdata
    const masterData = await api.get(`${API_PATH.MASTER}`, {
      params: { lang: language },
    });
    const masterDataReasponse = masterData.status === 200 && masterData.data.data.data.place.length > 0;

    //fetch event images min and max year
    const eventImageYears = await api.get(`${API_PATH.EVENT_IMAGE_YEARS}`, {});
    const eventImageYearsReasponse = eventImageYears.status === 200 && eventImageYears.data.data;
    const eventImageYearsData = eventImageYearsReasponse ? eventImageYears.data.data : null;
    const yearsArray = eventImageYearsData;

    // fetch image data
    let eventImageList: {
      data: { data: { years: number[]; events: eventImagesData[] } };
    } = { data: { data: { years: [], events: [] } } };

    try {
      eventImageList = await api.get(`${API_PATH.EVENT_IMAGES_LIST}/2025`, {
        params: {
          lang: language,
          pageIndex: 0,
          pageSize: 2,
        },
      });
    } catch (error) {
      console.error("Error fetching event images:", error);
    }

    const eventImageListReasponse = eventImageList.data.data;
    const availableyearsArray = eventImageList.data.data.years.sort((a, b) => b - a) || []; // Sort in descending order

    const DATA = {
      eventImages: eventImageListReasponse ? eventImageList.data.data.events : [],
      place: masterDataReasponse ? masterData.data.data.data.place : [],
      years: yearsArray.length > 0 ? yearsArray : [],
      selectedYear: selectedYear,
      availableyearsArray,
      title: lang === LANG.english ? "Event Images" : "ઇવેન્ટ ઇમેજેસ",
    };

    const etag = computeEtagFromData(DATA);

    const ifNoneMatch = request.headers.get("If-None-Match");
    if (ifNoneMatch === etag) {
      return new Response(null, {
        status: 304,
        headers: {
          Etag: etag,
        },
      });
    }

    return Response.json(DATA, {
      headers: { ...CACHED_PAGE.EVENT_IMAGES.HEADERS, ETag: etag },
    });
  } catch (error) {
    console.error("Error fetching data:", error);
    return Response.json({
      eventImages: [],
      place: [],
      years: [],
      availableyearsArray: [],
      selectedYear: 0,
      title: lang === LANG.english ? "Event Images" : "ઇવેન્ટ ઇમેજેસ",
    });
  }
};

export const headers: HeadersFunction = ({ loaderHeaders }) => loaderHeaders;

export const meta: MetaFunction = ({ location, params }) => {
  const pathname = location?.pathname || "/";
  const lang = params.lang || "en";

  const meta = getPageMeta("eventImages", lang);

  return metaService({
    title: meta.metaTitle,
    description: meta.metaDescription,
    ogTitle: meta.ogTitle,
    ogDescription: meta.ogDescription,
    ogUrl: pathname,
    canonicalLink: pathname,
    ogImg: meta.ogImage || FALLBACK_IMAGE_URL,
    ogWidth: meta.ogImageWidth || "1200",
    ogHeight: meta.ogImageHeight || "630",
    hreflangs: [
      { hrefLang: "x-default", href: `${pathname}` },
      { hrefLang: "en", href: `${pathname}` },
      { hrefLang: "gu", href: `${pathname}` },
    ],
  });
};

export const handle = {
  breadcrumb: (match: any) => {
    const lang = match.params.lang;
    return (
      <BreadCrubComponent
        breadCrub={[
          {
            path: homeRouteString(lang).path,
            title: homeRouteString(lang).home,
          },
        ]}
      />
    );
  },
};
const ImageGalleryPage = () => {
  const { showTopButton } = useDisplayStickyButton();

  const { years, eventImages, place, availableyearsArray } = useLoaderData<loaderData>();

  const { pathname } = useLocation();
  const { t } = useTranslation();
  const lang = useLanguageParams();
  const virtuoso = useRef<VirtuosoHandle | null>(null);
  const yearRefs = useRef<Record<number, HTMLElement | null>>({});
  const ticking = useRef(false);
  const isManualScroll = useRef(false);
  const manualScrollTimeout = useRef<NodeJS.Timeout | null>(null);

  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [eventImagesData, setEventImagesData] = useState<eventImagesData[]>(eventImages);
  const [availableyearsArrayData, setAvailableyearsArrayData] = useState<number[]>(availableyearsArray);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const { medium } = useFontStyle();

  const language = useMemo(() => (lang === LANG.gujarati ? LANG.gujarati : LANG.english), [lang]);

  const handleFetchNextPageData = async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    try {
      const getEventsFromThisArrray = availableyearsArrayData[availableyearsArrayData.length - 1] - 1;
      const response = await api.get(`${API_PATH.EVENT_IMAGES_LIST}/${getEventsFromThisArrray}`, {
        params: {
          lang: language,
          pageIndex: 0,
          pageSize: 2,
        },
      });

      const newEventImages = response.data?.data?.events;
      const newAvailableYears = response.data?.data?.years;

      // Check if new data exists, and then update state
      if (newEventImages && newAvailableYears) {
        setEventImagesData((prev) => ({ ...prev, ...newEventImages }));
        setAvailableyearsArrayData((prev) => [...prev, ...newAvailableYears]);
      } else {
        setHasMore(false); // Optionally, mark no more data
      }
    } catch (error) {
      console.error("Error fetching more data:", error);
      setHasMore(false); // Stop fetching if there's an error
      setLoading(false);
    } finally {
      setLoading(false); // Ensure loading state is set to false here
    }
  };

  const handleScroll = () => {
    // Don't update selectedYear during manual scroll
    if (isManualScroll.current) return;

    if (ticking.current) return;
    ticking.current = true;

    window.requestAnimationFrame(() => {
      // Only switch year when the heading itself has scrolled into / past the
      // sticky chip bar (≈150 px from the top).  Among all headings that have
      // crossed that line, pick the one closest to it (largest rect.top that
      // is still ≤ threshold) — that is the section the user is currently in.
      const threshold = 160;
      let activeYear: number | null = null;
      let bestTop = -Infinity;

      Object.entries(yearRefs.current).forEach(([yearStr, el]) => {
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= threshold && rect.top > bestTop) {
            bestTop = rect.top;
            activeYear = parseInt(yearStr);
          }
        }
      });

      if (activeYear !== null && activeYear !== selectedYear) {
        setSelectedYear(activeYear);
      }

      ticking.current = false;
    });
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [selectedYear]);

  const handleOnChipClick = async (targetYear: number) => {
    if (availableyearsArrayData.includes(targetYear)) {
      scrollToYear(targetYear);
      return;
    }

    setLoading(true);
    let currentYears = [...availableyearsArrayData];
    let found = false;
    let fetchYear = currentYears[currentYears.length - 1] - 1;

    while (!found && fetchYear >= 0) {
      // or some min year limit you have
      try {
        const response = await api.get(`${API_PATH.EVENT_IMAGES_LIST}/${fetchYear}`, {
          params: {
            lang: language,
            pageIndex: 0,
            pageSize: 2,
          },
        });

        const newEventImages = response.data?.data?.events;
        const newAvailableYears = response.data?.data?.years;

        if (!newAvailableYears || newAvailableYears.length === 0) {
          setHasMore(false);
          break;
        }

        // Append new years, removing duplicates
        currentYears = Array.from(new Set([...currentYears, ...newAvailableYears]));
        setAvailableyearsArrayData(currentYears);

        if (newEventImages) {
          setEventImagesData((prev) => ({ ...prev, ...newEventImages }));
        }

        if (currentYears.includes(targetYear)) {
          found = true;
          break;
        }

        fetchYear = currentYears[currentYears.length - 1] - 1; // Update next fetch year
      } catch (error) {
        console.error("Error loading more years:", error);
        break;
      }
    }
    setLoading(false);

    if (found) {
      scrollToYear(targetYear);
    } else {
      // Could not find year, maybe show a message or just do nothing
    }
  };

  const scrollToYear = (year: number) => {
    const index = years.indexOf(year);
    if (virtuoso.current && index !== -1) {
      // Set manual scroll flag to prevent automatic selection update
      isManualScroll.current = true;
      setSelectedYear(year); // Set the selected year immediately

      // Clear any existing timeout
      if (manualScrollTimeout.current) {
        clearTimeout(manualScrollTimeout.current);
      }

      virtuoso.current.scrollToIndex({
        index,
        behavior: "smooth",
        offset: -150,
      });

      // Re-enable automatic scroll tracking after scroll completes
      manualScrollTimeout.current = setTimeout(() => {
        isManualScroll.current = false;
      }, 1000); // Wait 1 second after scroll completes
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    setEventImagesData(eventImages);
    setAvailableyearsArrayData(availableyearsArray);
  }, [lang, eventImages, availableyearsArray]);

  return (
    <>
      {/* Sticky chip bar */}
      {/* {showTopButton ? ( */}
      <div className={cn(showTopButton ? "filter-chip-animation" : "filter-chip-non-animation")}>
        <Chip
          selectedValue={selectedYear ? Number(selectedYear) : years[0]}
          onClick={(year) => {
            handleOnChipClick(Number(year));
          }}
          chipData={years}
        />
      </div>

      <ClientOnly
        fallback={availableyearsArrayData.map((y) => (
          <div key={y} className="w-full relative px-3 pb-5 mx-2">
            <div className="h-full w-[3px] absolute left-0 top-4 bg-eventcardGradient opacity-100" />
            <div className="size-[10px] rounded-full absolute top-2 left-[-3px] bg-textColor" />
            <div className="w-full flex flex-col justify-start items-start gap-2">
              <h3 className={cn(" text-textColor body_text_l", medium)}>{y}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-5 px-3 w-full">
                {Array.isArray(eventImagesData[y]) &&
                  eventImagesData[y].map((image) => <ImageCard key={image.id} image={image} place={place} pathName={pathname} />)}
              </div>
            </div>
          </div>
        ))}
      >
        <div className="min-h-screen mb-5 overflow-hidden">
          <Virtuoso
            ref={virtuoso}
            style={{ height: "100%", width: "100%" }}
            data={availableyearsArrayData}
            endReached={handleFetchNextPageData}
            increaseViewportBy={200}
            useWindowScroll
            overscan={300}
            itemContent={(i, date) => {
              return (
                <div key={date} className="w-full relative px-3 pb-5 mx-2 " data-year={date}>
                  <div className="h-full w-[3px] absolute left-0 top-4 bg-eventcardGradient opacity-100" />
                  <div className="size-[10px] rounded-full absolute top-2 left-[-3px] bg-textColor" />
                  <div className="w-full flex flex-col justify-start items-start gap-2">
                    {/* year heading */}
                    <p className={cn("text-textColor body_text_l ", medium)} ref={(el) => (yearRefs.current[date] = el)} id={String(date)}>
                      {date}
                    </p>
                    {/* content */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-5 px-3 w-full">
                      {Array.isArray(eventImagesData[date]) &&
                        eventImagesData[date].map((image) => <ImageCard key={image.id} image={image} place={place} pathName={pathname} />)}
                    </div>
                  </div>
                </div>
              );
            }}
            components={{
              Footer: () => {
                return loading || hasMore ? (
                  <LoadingSpinner />
                ) : (
                  <NoHasMoreText className="text-textSecondaryColor" text={t("common.seeAllData", { data: t("event_images.title") })} />
                );
              },
            }}
          />
        </div>
      </ClientOnly>
    </>
  );
};

const ImageCard = memo(({ image, place, pathName }: imageCarddata) => {
  const placeName = place.find((p) => p.id === image.place);
  const date = getEventImageDate(image.from_date);
  const { LocalizedLink } = useLocale();
  const { medium, normalInter } = useFontStyle();
  const { t } = useTranslation();
  const isClient = useIsClient();

  const ImageCard = ({ isServer }: { isServer: boolean }) => (
    <ImageWithSkeleton
      url={
        image.img
          ? {
              small: image.img.m.url,
              medium: image.img.l.url,
              large: image.img.l.url,
              hd: image.img.hd.url,
              original: image.img.og.url,
            }
          : {
              small: FALLBACK_IMAGE_URL,
              medium: FALLBACK_IMAGE_URL,
              large: FALLBACK_IMAGE_URL,
              hd: FALLBACK_IMAGE_URL,
              original: FALLBACK_IMAGE_URL,
            }
      }
      alt={image.name}
      className={`w-full h-full object-cover shadow-md rounded-xl`}
      roundedClass="rounded-xl"
      blur_hash={image.img ? image.img.bh : BLUR_HASH}
      width={image.img ? image.img.m.w : 500}
      height={image.img ? image.img.m.h : 300}
      isPreloaded={isServer}
    />
  );

  return (
    <LocalizedLink to={`/${`event-images`}/${image.url_slug}`} className="overflow-y-hidden">
      <div className="flex flex-col gap-4 relative mt-3">
        <div className={cn("overflow-hidden rounded-xl", `aspect-[16/9]`)}>
          <ClientOnly fallback={<ImageCard isServer={true} />}>
            <ImageCard isServer={false} />
          </ClientOnly>
        </div>
        <div className="flex flex-col justify-start items-start w-full">
          <div className="flex justify-between items-center w-full pr-[6px]">
            <span className={cn("body_text_l text-textColor w-full truncate ", medium)} title={image.name}>
              {image?.name || t("event_images.no_title")}
            </span>

            <div>
              <ImageCardButtons shareTitle={image.name} shareUrl={pathName} />
            </div>
          </div>
          <div className="flex gap-[6px] justify-center items-center">
            <span className={cn("body_text_s text-textColor", normalInter)}>{`${t(`common.${date.month.toLowerCase()}`)} ${date.day}, ${
              date.year
            }`}</span>
            {placeName && (
              <>
                <div className="size-1 rounded-full bg-primaryColor"></div>
                <span className={cn("body_text_s text-textColor", normalInter)}>{placeName?.text}</span>
              </>
            )}
          </div>
        </div>
        {image.tag && (
          <div className="absolute top-1 left-1">
            <Tag text={image.tag ? image.tag : "New"} />
          </div>
        )}
      </div>
    </LocalizedLink>
  );
});

const ImageCardButtons = ({ shareUrl, shareTitle }: ImageCardButtonsProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  return (
    <>
      <div className="flex items-center justify-center w-[22px] h-[22px] bg-white rounded-full">
        <IconButton aria-label="more" aria-controls="long-menu" aria-haspopup="true" onClick={(e) => handleMenuClick(e)}>
          <CgMoreVerticalAlt size={18} />
        </IconButton>
      </div>
      <Menu
        id="long-menu"
        anchorEl={anchorEl}
        aria-modal="false"
        keepMounted
        disableScrollLock
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        sx={{
          "& .MuiMenuItem-root": {
            paddingX: 0.9,
            minHeight: "auto",
          },
          "& .MuiPaper-root": {
            padding: 0,
            marginLeft: -2,
          },
        }}
      >
        <MenuItem
          sx={{
            height: "auto",
            width: "auto",
            borderRadius: 1,
            display: "flex",
            padding: 1.5,
            paddingX: 3,
            flexDirection: "column",
            gap: 1,
          }}
        >
          <div className="flex justify-start items-center gap-2">
            <div className="size-8 rounded-full bg-transparent flex justify-center items-center">
              <MdFavoriteBorder size={20} color="#D05E2D" />
            </div>
            <span className="font-abcArizonaFlareVariable text-textColor pr-3">Favorite</span>
          </div>
        </MenuItem>
        <MenuItem
          sx={{
            height: "auto",
            width: "auto",
            borderRadius: 1,
            display: "flex",
            padding: 1.5,
            paddingX: 3,
            flexDirection: "column",
            gap: 1,
          }}
          onClick={handleMenuClose}
        >
          <ShareBotton shareUrl={shareUrl} shareTitle={shareTitle} text={true} bgColor="transparent" />
        </MenuItem>
      </Menu>
    </>
  );
};

export function ErrorBoundary({ error }: { error: Error }) {
  console.error(error);
  return <NodataFound text={"No event found for this year"} />;
}

export default ImageGalleryPage;
